import { format, parse } from 'date-fns'
import {
  object,
  date,
  nullable,
  boolean,
  type InferOutput,
  number,
  string,
  array,
  picklist,
  optional
} from 'valibot'

import { SHIPMENT_DELIVERY_STATUS_TYPE } from '@/libs/api/types'
import { filterObjectEmpty } from '@/libs/utils'

import { type FetchNotDispatchedShipmentOrdersRequestQuery } from './fetch-not-dispatched-shipment-orders-request-query'

export const notDispatchedShipmentOrdersSearchSchema = object({
  /**
   * 発送日 From / To。null のときは配車板で選択中の日付 1 日だけを対象とする。
   * 配車板の日付が動いたら範囲指定は破棄される（配車板の日付と食い違う一覧を残さないため）。
   */
  shippingDateFrom: nullable(date()),
  shippingDateTo: nullable(date()),
  /** 着日 From / To。null なら着日で絞り込まない */
  deliveryDateFrom: nullable(date()),
  deliveryDateTo: nullable(date()),
  /** 配送種別ID */
  shipmentCategoryId: nullable(array(number())),
  /** 配送ステータス（11段階・複数指定でOR検索） */
  deliveryStatus: nullable(
    array(picklist(Object.values(SHIPMENT_DELIVERY_STATUS_TYPE)))
  ),
  /** 荷主ID */
  customerId: nullable(array(number())),
  /** 車種ID */
  truckModelId: nullable(array(number())),
  /** 発地 地点ID */
  originSiteId: nullable(array(number())),
  /** 着地 地点ID */
  destSiteId: nullable(array(number())),
  /** 発地 エリアなし地点を絞り込む */
  originSiteIsNull: nullable(boolean()),
  /** 着地 エリアなし地点を絞り込む */
  destSiteIsNull: nullable(boolean()),
  /** 発地・着地ペア絞り込みの表示ラベル（チップ表示用、API には送らない） */
  sitePairsDirectionLabel: nullable(string()),
  /** 発地・着地ペア絞り込み（複数方面選択用）。設定時は originSiteId/destSiteId/originSiteIsNull/destSiteIsNull は無視される */
  sitePairs: nullable(
    array(
      object({
        originSiteIds: optional(array(number())),
        originSiteIsNull: optional(boolean()),
        destSiteIds: optional(array(number())),
        destSiteIsNull: optional(boolean())
      })
    )
  ),
  /** 重量(〜t以下) */
  maxWeightTon: nullable(number()),
  /** 重量(〜t以上) */
  minWeightTon: nullable(number()),
  /** 配送依頼ナンバー（複数指定でOR検索） */
  orderNos: nullable(array(string())),
  /**
   * カスタムキー1〜5（#3463 VOC_PJ-829/851、拠点マスタで isVisible 指定時のみ検索項目として表示）。
   * orderNos と同型の複数値OR検索（同一項目内OR・別項目間AND）。
   */
  customKey1s: nullable(array(string())),
  customKey2s: nullable(array(string())),
  customKey3s: nullable(array(string())),
  customKey4s: nullable(array(string())),
  customKey5s: nullable(array(string())),
  /** フリーワード検索（備考・荷主名・車種名・地点名を横断検索） */
  freeWord: nullable(string()),
  /** 発時間制限 */
  departureTimeRestriction: nullable(
    object({
      startTime: string(),
      rangeHours: nullable(number())
    })
  )
})

/**
 * 配送依頼一覧の検索フォームの値
 */
export type NotDispatchedShipmentOrdersSearchFormValue = InferOutput<
  typeof notDispatchedShipmentOrdersSearchSchema
>

/**
 * 配送依頼一覧の検索フォームの値を一覧取得のクエリに変換
 */
export const mapToNotDispatchedShipmentOrdersSearchQuery = (
  {
    shippingDateFrom,
    shippingDateTo,
    deliveryDateFrom,
    deliveryDateTo,
    customerId,
    shipmentCategoryId,
    deliveryStatus,
    truckModelId,
    originSiteId,
    destSiteId,
    originSiteIsNull,
    destSiteIsNull,
    sitePairs,
    departureTimeRestriction,
    ...formValue
  }: NotDispatchedShipmentOrdersSearchFormValue,
  dcId: number,
  shippingDate?: string
): FetchNotDispatchedShipmentOrdersRequestQuery => {
  const query: FetchNotDispatchedShipmentOrdersRequestQuery & {
    departureTimeRestrictionStart?: string
    departureTimeRestrictionRangeMinutes?: number
  } = {
    orderNos:
      formValue.orderNos && formValue.orderNos.length > 0
        ? formValue.orderNos
        : undefined,
    // #3463 VOC_PJ-829/851: カスタムキー1〜5（orderNos と同型の複数値OR検索）
    customKey1s:
      formValue.customKey1s && formValue.customKey1s.length > 0
        ? formValue.customKey1s
        : undefined,
    customKey2s:
      formValue.customKey2s && formValue.customKey2s.length > 0
        ? formValue.customKey2s
        : undefined,
    customKey3s:
      formValue.customKey3s && formValue.customKey3s.length > 0
        ? formValue.customKey3s
        : undefined,
    customKey4s:
      formValue.customKey4s && formValue.customKey4s.length > 0
        ? formValue.customKey4s
        : undefined,
    customKey5s:
      formValue.customKey5s && formValue.customKey5s.length > 0
        ? formValue.customKey5s
        : undefined,
    freeWord: formValue.freeWord ?? undefined,
    minWeightTon: formValue.minWeightTon ?? undefined,
    maxWeightTon: formValue.maxWeightTon ?? undefined,
    shippingDateFrom: shippingDateFrom
      ? format(shippingDateFrom, 'yyyy-MM-dd')
      : undefined,
    shippingDateTo: shippingDateTo
      ? format(shippingDateTo, 'yyyy-MM-dd')
      : undefined,
    deliveryDateFrom: deliveryDateFrom
      ? format(deliveryDateFrom, 'yyyy-MM-dd')
      : undefined,
    deliveryDateTo: deliveryDateTo
      ? format(deliveryDateTo, 'yyyy-MM-dd')
      : undefined,
    customerIds: customerId && customerId.length > 0 ? customerId : undefined,
    truckModelIds:
      truckModelId && truckModelId.length > 0 ? truckModelId : undefined,
    originSiteIds: originSiteId ? originSiteId : undefined,
    destSiteIds: destSiteId ? destSiteId : undefined,
    originSiteIsNull: originSiteIsNull ?? undefined,
    destSiteIsNull: destSiteIsNull ?? undefined,
    sitePairs:
      sitePairs && sitePairs.length > 0 ? JSON.stringify(sitePairs) : undefined,
    shipmentCategoryIds: shipmentCategoryId ? shipmentCategoryId : undefined,
    deliveryStatus:
      deliveryStatus && deliveryStatus.length > 0 ? deliveryStatus : undefined,
    dcIds: [dcId]
  }

  if (departureTimeRestriction) {
    const { startTime, rangeHours } = departureTimeRestriction

    // 配送日を使用
    const baseDate = shippingDate || format(new Date(), 'yyyy-MM-dd')

    // 日本時間として時刻を設定し、UTCに変換
    const [hours, minutes] = startTime.split(':')
    const jstDateTime = parse(
      `${baseDate} ${hours}:${minutes}`,
      'yyyy-MM-dd HH:mm',
      new Date()
    )

    // 日本時間をUTCに変換してISO形式で送信
    // データベースは日本時間で保存されているが、APIはUTC時刻を期待している
    query.departureTimeRestrictionStart = jstDateTime.toISOString()

    if (rangeHours) {
      query.departureTimeRestrictionRangeMinutes = rangeHours * 60
    }
  }

  return filterObjectEmpty(query)
}

/**
 * 一覧取得のクエリを配送依頼一覧の検索フォームの値に変換
 */
export const mapToNotDispatchedShipmentOrdersSearchFormValue = (
  query: FetchNotDispatchedShipmentOrdersRequestQuery & {
    departureTimeRestrictionStart?: string
    departureTimeRestrictionRangeMinutes?: number
  }
): NotDispatchedShipmentOrdersSearchFormValue => {
  let departureTimeRestriction = null
  if (query?.departureTimeRestrictionStart) {
    // サーバーからのUTC時刻を日本時間に変換
    const utcDate = new Date(query.departureTimeRestrictionStart)

    // JavaScriptのDateは自動的にローカルタイムゾーン（JST）に変換される
    const hours = utcDate.getHours().toString().padStart(2, '0')
    const minutes = utcDate.getMinutes().toString().padStart(2, '0')

    departureTimeRestriction = {
      startTime: `${hours}:${minutes}`,
      rangeHours: query.departureTimeRestrictionRangeMinutes
        ? query.departureTimeRestrictionRangeMinutes / 60
        : null
    }
  }

  return {
    shippingDateFrom: query?.shippingDateFrom
      ? parse(query.shippingDateFrom, 'yyyy-MM-dd', new Date())
      : null,
    shippingDateTo: query?.shippingDateTo
      ? parse(query.shippingDateTo, 'yyyy-MM-dd', new Date())
      : null,
    deliveryDateFrom: query?.deliveryDateFrom
      ? parse(query.deliveryDateFrom, 'yyyy-MM-dd', new Date())
      : null,
    deliveryDateTo: query?.deliveryDateTo
      ? parse(query.deliveryDateTo, 'yyyy-MM-dd', new Date())
      : null,
    customerId: query?.customerIds?.length ? query.customerIds : null,
    shipmentCategoryId: query?.shipmentCategoryIds?.length
      ? query.shipmentCategoryIds
      : null,
    deliveryStatus: query?.deliveryStatus?.length ? query.deliveryStatus : null,
    truckModelId: query?.truckModelIds?.length ? query.truckModelIds : null,
    originSiteId: query?.originSiteIds?.length ? query.originSiteIds : null,
    destSiteId: query?.destSiteIds?.length ? query.destSiteIds : null,
    originSiteIsNull: query?.originSiteIsNull ?? null,
    destSiteIsNull: query?.destSiteIsNull ?? null,
    sitePairs: null,
    sitePairsDirectionLabel: null,
    // maxWeightTonのみ 0t以下の時の処理が存在するため、厳密にundefinedとnullをチェック
    maxWeightTon:
      query?.maxWeightTon !== undefined && query?.maxWeightTon !== null
        ? query.maxWeightTon
        : null,
    minWeightTon: query?.minWeightTon ? query.minWeightTon : null,
    orderNos: query?.orderNos?.length ? query.orderNos : null,
    // #3463 VOC_PJ-829/851: カスタムキー1〜5
    customKey1s: query?.customKey1s?.length ? query.customKey1s : null,
    customKey2s: query?.customKey2s?.length ? query.customKey2s : null,
    customKey3s: query?.customKey3s?.length ? query.customKey3s : null,
    customKey4s: query?.customKey4s?.length ? query.customKey4s : null,
    customKey5s: query?.customKey5s?.length ? query.customKey5s : null,
    freeWord: query?.freeWord ? query.freeWord : null,
    departureTimeRestriction
  }
}
